<template>
    <div>
        <section class="w-full bg-blue-100">
            <div class="bg-white rounded-b-2xl">
                <div class="self-gutter">
                    <div class="self-gutter-y self-content-sm">
                        <ReportNavigation v-if="this.$route.path.startsWith(`/${$i18n.locale}/reports`)" />
                        <h1 class="title-2xl" v-html="$t('co2_report.title')"></h1>
                        <AlertSummary
                            v-if="!generateReportOk"
                            :main-text="$t('error.main_text')"
                            :secondary-text="$t('error.secondary_text')"
                            alert-classes="mt-24 e5-form-error-summary--error"
                        />

                        <div class="pt-24 self-hero-intro expandable-description">
                            <p v-html="$t('co2_report.desc')"></p>
                        </div>
                        <!-- CO2 report form-->
                        <div class="pt-24 self-form-wrap">
                            <div class="w-full sm:w-5/12 self-form-wrap-item" :class="{ 'e5-error': !generateReportOk }">
                                <label class="self-form-item-label" for="calendar"
                                    >{{ $t('reports.form.period') }}:<span class="e5-form-field-required">*</span></label
                                >
                                <div class="relative">
                                    <button
                                        ref="calendarButton"
                                        id="calendar"
                                        class="e5-input-calendar"
                                        :class="{ 'e5-error': !generateReportOk }"
                                        aria-haspopup="true"
                                        aria-expanded="false"
                                        @click="dropdown('calendarButton')"
                                    >
                                        <span :class="{ 'text-alert': !generateReportOk }">
                                            {{
                                                ServiceByDateRequest.service.servicestartdate || ServiceByDateRequest.service.serviceenddate
                                                    ? selectedDateRange
                                                    : $t('reports.form.period_placeholder')
                                            }}</span
                                        >
                                    </button>

                                    <div class="e5-input-icon">
                                        <CalendarSVG />
                                    </div>
                                    <DropDownCalendar v-model="selectedDateRange" @input="handleDateChange" />
                                </div>
                                <a class="hover:text-blue-400 cursor-pointer" @click="quickSelectDates('currentMonth')">{{
                                    $t('reports.form.current_month')
                                }}</a>
                                |
                                <a class="hover:text-blue-400 cursor-pointer" @click="quickSelectDates('lastMonth')">{{
                                    $t('reports.form.last_month')
                                }}</a>
                                |
                                <a class="hover:text-blue-400 cursor-pointer" @click="quickSelectDates('currentYear')">{{
                                    $t('reports.form.current_year')
                                }}</a>
                                |
                                <a class="hover:text-blue-400 cursor-pointer" @click="quickSelectDates('lastYear')">{{
                                    $t('reports.form.last_year')
                                }}</a>
                            </div>

                            <div class="self-form-wrap-actions justify-end">
                                <div class="flex xs-down:justify-end xs-down:pt-24">
                                    <a class="mr-24 self-btn-secondary hover:cursor-pointer" @click="clearAll">{{
                                        $t('reports.form.clear_all')
                                    }}</a>

                                    <button class="self-btn-primary" :disabled="loading" @click="generateOnClick(ServiceByDateRequest)">
                                        {{ $t('reports.form.generate_report') }}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
        <!--    Not empty response    -->
        <article v-if="co2ReportResponse?.length && requestSent">
            <div id="resultsTable" class="relative bg-blue-100 rounded-b-2xl self-gutter-y">
                <div class="self-gutter">
                    <div class="self-content">
                        <div class="self-table-info justify-end">
                            <div class="flex items-center sm-down:w-full sm-down:justify-between sm-down:pt-16">
                                <div class="self-table-info-entries-select"></div>

                                <div class="relative inline-block">
                                    <div class="relative inline-block">
                                        <button
                                            ref="exportButton"
                                            class="self-btn-plain"
                                            id="export-report"
                                            aria-haspopup="true"
                                            aria-expanded="false"
                                            @click="dropdown('exportButton')"
                                        >
                                            <span class="self-btn-plain__text">{{ $t('reports.table.export') }}</span>
                                            <span class="self-btn-plain__icon self-moreinfo-icon">
                                                <IconArrowForwardSVG />
                                            </span>
                                        </button>

                                        <div class="e5-dropdown e5-dropdown-xs" aria-labelledby="export-report">
                                            <div class="p-10">
                                                <a class="py-5 self-text-link hover:cursor-pointer" @click="download('.xlsx')"
                                                    >Excel (XLSX)</a
                                                >
                                                <a class="py-5 self-text-link hover:cursor-pointer" @click="download('.csv')">CSV</a>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <table class="w-full self-table">
                            <thead>
                                <tr>
                                    <th>
                                        <span>{{ $t('reports.table.booking_no') }}</span>
                                    </th>
                                    <th>
                                        <span>{{ $t('reports.table.invoice_date') }}</span>
                                    </th>
                                    <th>
                                        <span>
                                            <select id="passengerSelect" class="self-table-header-select">
                                                <option value="">{{ $t('reports.table.passenger_name') }}</option>
                                                <option v-for="passenger in uniquePassengers" :key="passenger" :value="passenger">
                                                    {{ passenger }}
                                                </option>
                                            </select>
                                        </span>
                                    </th>
                                    <th>
                                        <span>{{ $t('reports.table.service') }}</span>
                                    </th>
                                    <th>
                                        <span>{{ $t('reports.table.airline') }}</span>
                                    </th>
                                    <th>
                                        <span>{{ $t('reports.table.departure_airport') }}</span>
                                    </th>
                                    <th>
                                        <span>{{ $t('reports.table.arrival_airport') }}</span>
                                    </th>
                                    <th>
                                        <span>{{ $t('reports.table.flight_number') }}</span>
                                    </th>
                                    <th class="date">
                                        <span>{{ $t('reports.table.departure_date') }}</span>
                                    </th>
                                    <th class="time">
                                        <span>{{ $t('reports.table.departure_time') }}</span>
                                    </th>
                                    <th class="date">
                                        <span>{{ $t('reports.table.arrival_date') }}</span>
                                    </th>
                                    <th class="time">
                                        <span>{{ $t('reports.table.arrival_time') }}</span>
                                    </th>
                                    <th>
                                        <span>{{ $t('reports.table.mileage') }}</span>
                                    </th>
                                    <th><span v-html="$t('reports.table.co2')"></span></th>
                                    <th><span v-html="$t('reports.table.booking_class')"></span></th>
                                </tr>
                            </thead>
                            <tbody>
                                <!--                        TODO: If item.co2 !== null && item.mileage !== null. Tabelis on vaja näidata ainult neid teenuseid, kus need väärtused ka olemas (ei ole null)--->
                                <tr v-for="(item, index) in co2ReportResponse" :key="index">
                                    <td>{{ item.bkgRefno }}</td>
                                    <td>{{ formatDate(item.invoiceDate) }}</td>
                                    <td>{{ (item.firstName + ' ' + item.lastName).trim() }}</td>
                                    <td>{{ item.productName }}</td>
                                    <td>{{ item.supplierAlias }}</td>
                                    <td>
                                        {{ item.departureAirportName
                                        }}{{ item.departureAirportCode?.length ? ` (${item.departureAirportCode})` : '' }}
                                    </td>
                                    <td>
                                        {{ item.destinationAirportName
                                        }}{{ item.destinationAirportCode?.length ? ` (${item.destinationAirportCode})` : '' }}
                                    </td>
                                    <td>{{ item.flightNumber }}</td>
                                    <td>{{ formatDate(item.departureDateTime) }}</td>
                                    <td>{{ formatTime(item.departureDateTime) }}</td>
                                    <td>{{ formatDate(item.arrivalDateTime) }}</td>
                                    <td>{{ formatTime(item.arrivalDateTime) }}</td>
                                    <td>{{ item.mileage }}</td>
                                    <td>{{ item.co2Emissions }}</td>
                                    <td>{{ item.bookingClass }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </article>
        <!--Empty response-->
        <article v-else>
            <section id="resultsTable" class="relative bg-blue-100 rounded-b-2xl self-gutter-y">
                <div class="self-gutter">
                    <div class="self-content-sm">
                        <span class="block title-2xl pt-32 pb-[300px] text-black font-normal" v-if="requestSent && responseReceived">{{
                            $t('reports.table.no_results')
                        }}</span>
                        <span class="block title-2xl pt-32 pb-[300px] text-black font-normal" v-else-if="requestSent">
                            <span class="loading-animation"
                                >{{ $t('reports.table.searching')
                                }}<span class="loading-dots">
                                    <span class="dot">.</span>
                                    <span class="dot">.</span>
                                    <span class="dot">.</span>
                                </span></span
                            ></span
                        >
                    </div>
                </div>
            </section>
        </article>
    </div>
</template>

<script lang="ts">
import { Component, Vue } from 'vue-property-decorator';
import { ICo2ReportResponseEntity, IServiceByDateRequest } from '@/domain/IReport';
import DropDownCalendar from '@/components/DropDownCalendar.vue';
import CalendarSVG from '@/assets/static/icons-src/icon-calendar.svg';
import IconArrowForwardSVG from '@/assets/static/icons-src/icon-arrow-forward.svg';
import ArrowLeftSVG from '@/assets/static/icons-src/est-arrow-left.svg';
import LogoIllustrationSVG from '@/assets/static/icons-src/logo-illustration.svg';
import store from '@/store';
import { IService } from '@/domain/IService';
import $ from 'jquery';
import UtilsMixin from '@/scripts/utils';
import moment from 'moment';
import ReportNavigation from '@/components/ReportNavigation.vue';
import AlertSummary from '@/components/AlertSummary.vue';
import { eventBus } from '@/../eventBus';
import * as XLSX from 'xlsx';

@Component({
    components: {
        ReportNavigation,
        DropDownCalendar,
        CalendarSVG,
        IconArrowForwardSVG,
        ArrowLeftSVG,
        LogoIllustrationSVG,
        AlertSummary
    },
    metaInfo: {
        title: 'CO₂ report'
    },
    mixins: [UtilsMixin]
})
export default class Co2Report extends Vue {
    // Properties
    co2ReportResponse: ICo2ReportResponseEntity[] = [];
    selectedDateRange = '';
    requestSent = false;
    private initExtraTable = true;
    generateReportOk = true;
    private extraTable = null;
    responseReceived = false;
    loading = false;

    ServiceByDateRequest: Partial<IServiceByDateRequest> = {
        service: {
            id: '',
            servicestartdate: '',
            serviceenddate: '',
            bookingreferencenumber: '',
            servicedestinationcountry: '',
            servicedestinationcity: '',
            productname: ''
        } as unknown as IService,
        customerid: store.state.customerId
    };

    // Computed properties

    get uniquePassengers(): string[] {
        return Array.from(new Set(this.co2ReportResponse.map((item) => (item.firstName + ' ' + item.lastName).trim())))
            .filter(Boolean)
            .sort();
    }

    // Methods

    formatTime(dateTime) {
        return moment(dateTime).format('HH.mm');
    }

    async generateOnClick(ServiceByDateRequest: Partial<IServiceByDateRequest>) {
        if (this.selectedDateRange.split('—').length !== 2) {
            this.generateReportOk = false;
            this.scrollToAlertMessage();
        } else {
            this.scrollToResultsTable();
            this.generateReportOk = true;
            this.requestSent = true;
            this.initExtraTable = true;
            this.destroyExtraTable();
            this.responseReceived = false;
            this.loading = true;
            this.co2ReportResponse = await store.dispatch('getCo2ReportByDate', ServiceByDateRequest);
            this.loading = false;
            this.responseReceived = true;
        }
    }

    // Type Declarations for TypeScript to tell that these methods exist in utils.ts
    scrollToAlertMessage!: () => void;
    scrollToResultsTable!: () => void;

    clearAll() {
        this.ServiceByDateRequest = {
            service: {
                id: '',
                servicestartdate: '',
                serviceenddate: '',
                bookingreferencenumber: '',
                servicedestinationcountry: '',
                servicedestinationcity: '',
                productname: ''
            } as unknown as IService,
            customerid: store.state.customerId
        };
        this.requestSent = false;
        this.$root.$emit('reset-calendar');
        this.selectedDateRange = '';
        this.destroyExtraTable();
        this.initExtraTable = true;
        this.responseReceived = false;
        this.co2ReportResponse = [];
    }

    private createExtraTable() {
        $.fn.dataTable.ext.errMode = 'throw';

        // Define a custom sorting plugin for formatted total sum
        $.fn.dataTable.ext.type.order['formatted-num-asc'] = function (a, b) {
            a = parseFloat(a.replace(/[^\d.-]/g, '').replace(/,/g, '')) || 0;
            b = parseFloat(b.replace(/[^\d.-]/g, '').replace(/,/g, '')) || 0;
            return a - b;
        };

        $.fn.dataTable.ext.type.order['formatted-num-desc'] = function (a, b) {
            a = parseFloat(a.replace(/[^\d.-]/g, '').replace(/,/g, '')) || 0;
            b = parseFloat(b.replace(/[^\d.-]/g, '').replace(/,/g, '')) || 0;
            return b - a;
        };

        // Sorting function for dates in 'DD.MM.YYYY' format
        $.fn.dataTable.ext.type.order['moment-date-asc'] = function (a, b) {
            return moment(a, 'DD.MM.YYYY').isAfter(moment(b, 'DD.MM.YYYY')) ? 1 : -1;
        };

        $.fn.dataTable.ext.type.order['moment-date-desc'] = function (a, b) {
            return moment(a, 'DD.MM.YYYY').isBefore(moment(b, 'DD.MM.YYYY')) ? 1 : -1;
        };

        // Sorting function for times in 'HH.mm' format
        $.fn.dataTable.ext.type.order['moment-time-asc'] = function (a, b) {
            return moment(a, 'HH.mm').isAfter(moment(b, 'HH.mm')) ? 1 : -1;
        };

        $.fn.dataTable.ext.type.order['moment-time-desc'] = function (a, b) {
            return moment(a, 'HH.mm').isBefore(moment(b, 'HH.mm')) ? 1 : -1;
        };

        $.fn.dataTable.ext.search.push(function (settings, data, dataIndex) {
            const selectedPassenger = $('#passengerSelect').val();
            if (settings.aoColumns.length < 3) {
                return true;
            }
            if (selectedPassenger === '') {
                return true;
            }
            const name = data[2].trim() || '';
            return name === selectedPassenger || name.includes(selectedPassenger);
        });

        this.destroyExtraTable();
        this.extraTable = $('.self-table').DataTable({
            dom: '<il<t>p>',
            paging: true,
            info: true,
            scrollX: true,
            order: [],
            language: {
                lengthMenu: this.$t('datatable.lengthMenu') as string,
                info: this.$t('datatable.info') as string,
                infoEmpty: this.$t('datatable.infoEmpty') as string,
                zeroRecords: this.$t('datatable.zeroRecords') as string,
                paginate: {
                    first: this.$t('datatable.first') as string,
                    last: this.$t('datatable.last') as string,
                    next: this.$t('datatable.next') as string,
                    previous: this.$t('datatable.previous') as string
                }
            },
            columnDefs: [
                {
                    type: 'formatted-num',
                    targets: 10
                },
                {
                    type: 'moment-date',
                    targets: 'date'
                },
                {
                    type: 'moment-time',
                    targets: 'time'
                }
            ]
        });

        // Clear the previously appended elements
        const entriesCount = document.querySelector('.self-table-info-entries-count');
        const lengthSelect = document.querySelector('.self-table-info-entries-select');

        if (entriesCount) {
            entriesCount.innerHTML = '';
        }

        if (lengthSelect) {
            lengthSelect.innerHTML = '';
        }

        const tables = document.querySelectorAll('.dataTables_wrapper');

        tables.forEach((table) => {
            const prev = table.previousElementSibling;
            const entriesCount = prev.querySelector('.self-table-info-entries-count');
            const lengthSelect = prev.querySelector('.self-table-info-entries-select');

            $(table).find('.dataTables_info').appendTo(entriesCount);
            $(table).find('.dataTables_length').appendTo(lengthSelect);
        });
        const selectTags = document.querySelectorAll('.self-table-header-select');
        selectTags.forEach((selectTag) => {
            if (selectTag) {
                selectTag.addEventListener('click', (event) => {
                    event.stopPropagation();
                });
            }
        });
        $('#passengerSelect').on('change', () => {
            this.extraTable.draw();
        });
        $('#destinationSelect').on('change', () => {
            this.extraTable.draw();
        });
    }

    private destroyExtraTable() {
        if (this.extraTable != null) {
            this.extraTable.destroy();
            this.extraTable = null;
        }
    }

    async onLocaleChange() {
        this.createExtraTable();
    }

    // Method to retrieve table data as an object
    private getTableData() {
        const data = this.extraTable.rows().data().toArray();
        const columns = this.extraTable
            .columns()
            .header()
            .toArray()
            .map((col) => col.innerText.split('\n')[0]);

        return data.map((row) => {
            const obj = {};
            row.forEach((cell, index) => {
                const isMileageOrCo2Column = index === 12 || index === 13;
                if (!isMileageOrCo2Column) {
                    obj[columns[index]] = cell;
                    return;
                }

                const numericValue = parseFloat(String(cell).replace(/\s/g, '').replace(',', '.'));
                obj[columns[index]] = Number.isNaN(numericValue) ? null : numericValue;
            });
            return obj;
        });
    }

    private forceNumericExportColumns(sheet: XLSX.WorkSheet, dataRowCount: number) {
        const mileageColumnIndex = 12;
        const co2ColumnIndex = 13;
        const lastDataRowIndex = dataRowCount; // zero-based index where row 0 is the header row

        for (let rowIndex = 1; rowIndex <= lastDataRowIndex; rowIndex++) {
            [mileageColumnIndex, co2ColumnIndex].forEach((columnIndex) => {
                const cellAddress = XLSX.utils.encode_cell({ c: columnIndex, r: rowIndex });
                const cell = sheet[cellAddress];

                if (!cell || cell.v === null || cell.v === undefined || cell.v === '') {
                    return;
                }

                const numericValue =
                    typeof cell.v === 'number' ? cell.v : parseFloat(String(cell.v).replace(/\s/g, '').replace(',', '.'));
                if (Number.isNaN(numericValue)) {
                    return;
                }

                cell.v = numericValue;
                cell.t = 'n';
                cell.z = columnIndex === mileageColumnIndex ? '0' : '0.00';
            });
        }
    }

    download(DownloadFileType) {
        const tableData = this.getTableData();
        const data = XLSX.utils.json_to_sheet(tableData);
        this.forceNumericExportColumns(data, tableData.length);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, data, 'data');
        if (DownloadFileType === '.xlsx') {
            XLSX.writeFile(wb, 'co2-report.xlsx');
        } else if (DownloadFileType === '.csv') {
            XLSX.writeFile(wb, 'co2-report.csv', { bookType: 'csv' });
        }
    }

    // ============ Lifecycle methods ==========

    async mounted() {
        eventBus.$on('dataFetched', this.onLocaleChange);
        this.clearAll();
    }

    beforeDestroy() {
        eventBus.$off('dataFetched', this.onLocaleChange);
    }

    updated(): void {
        if (this.co2ReportResponse?.length && this.requestSent && this.initExtraTable) {
            this.createExtraTable();
            this.initExtraTable = false;
        }
    }

    destroyed(): void {
        $.fn.dataTable.ext.search.length = 0; // Clear the array of custom search functions
        this.clearAll();
    }
}
</script>

<style scoped></style>
